import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  UserOutlined,
  KeyOutlined,
  MailOutlined,
  SafetyOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { useIsFormSubmittable } from '@/shared/hooks/useIsFormSubmittable';
import { userApiService } from '@/features/users/services/userApiService';
import {
  type User,
  type Role,
  type Site,
  type TrustedDevice,
  ROLE_ID,
  isSiteRestrictedRole,
} from '@/features/users/types/user';
import { formatDateTime } from '@/shared/utils/dateFormatter';

const { Title, Text } = Typography;

// ロール名から色を決定
const ROLE_TAG_COLORS: Record<number, string> = {
  [ROLE_ID.DEV_USER]: 'red',
  [ROLE_ID.MCM_USER]: 'blue',
  [ROLE_ID.SITE_USER]: 'green',
  [ROLE_ID.SITE_VIEWER]: 'cyan',
};

const getRoleTagColor = (roleId: number): string => {
  return ROLE_TAG_COLORS[roleId] ?? 'default';
};

// デフォルトセッション期間（分）
const DEFAULT_SESSION_DURATION_MINUTES = 180; // 3時間
/** セッション期間の最小値（分） */
const SESSION_DURATION_MIN_MINUTES = 1;
/** セッション期間の最大値（分, 1年） */
const SESSION_DURATION_MAX_MINUTES = 525600;

// ロール別デフォルトセッション期間を取得
const getDefaultSessionDurationByRole = (roleId: number): number | null => {
  if (roleId === ROLE_ID.SITE_VIEWER) {
    return null; // 閲覧ユーザーは無制限
  }
  return DEFAULT_SESSION_DURATION_MINUTES;
};

interface UserFormValues {
  userName: string;
  password?: string;
  roleId: number;
  siteId?: number | null;
  isActive: boolean;
  sessionDurationMinutes?: number | null;
  // 2FA用フィールド
  email?: string;
  twoFactorEnabled?: boolean;
}

interface PasswordResetFormValues {
  newPassword: string;
  confirmPassword: string;
}

export const UserManagementPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [users, setUsers] = useState<readonly User[]>([]);
  const [roles, setRoles] = useState<readonly Role[]>([]);
  const [sites, setSites] = useState<readonly Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [form] = Form.useForm<UserFormValues>();
  const isUserFormSubmittable = useIsFormSubmittable({ form, isFormVisible: isModalVisible });
  const [passwordResetForm] = Form.useForm<PasswordResetFormValues>();
  const [isPasswordResetModalVisible, setIsPasswordResetModalVisible] = useState(false);
  const [passwordResetTargetUser, setPasswordResetTargetUser] = useState<User | null>(null);
  const [isPasswordResetSubmitting, setIsPasswordResetSubmitting] = useState(false);

  // 2FA確認ダイアログ関連
  const [isTwoFactorConfirmVisible, setIsTwoFactorConfirmVisible] = useState(false);

  // 信頼済みデバイス関連
  const [trustedDevices, setTrustedDevices] = useState<readonly TrustedDevice[]>([]);
  const [isTrustedDevicesLoading, setIsTrustedDevicesLoading] = useState(false);
  const [isTrustedDevicesModalVisible, setIsTrustedDevicesModalVisible] = useState(false);
  const [trustedDevicesTargetUser, setTrustedDevicesTargetUser] = useState<User | null>(null);

  // Form.useWatchでemailフィールドの値を監視（リアクティブに更新される）
  const emailValue = Form.useWatch('email', form);

  // データ取得
  const loadData = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const [usersData, rolesData, sitesData] = await Promise.all([
        userApiService.getAllUsers(),
        userApiService.getAllRoles(),
        userApiService.getAllSites(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setSites(sitesData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(`データの取得に失敗しました: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 新規追加
  const handleAdd = (): void => {
    setEditingUser(null);
    setSelectedRoleId(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setIsModalVisible(true);
  };

  // 編集
  const handleEdit = (user: User): void => {
    setEditingUser(user);
    setSelectedRoleId(user.roleId);
    form.setFieldsValue({
      userName: user.userName,
      roleId: user.roleId,
      siteId: user.siteId,
      isActive: user.isActive,
      sessionDurationMinutes: user.sessionDurationMinutes,
      // 2FA用フィールド追加
      email: user.email ?? '',
      twoFactorEnabled: user.twoFactorEnabled,
    });
    setIsModalVisible(true);
  };

  // ロール変更時
  const handleRoleChange = (roleId: number): void => {
    setSelectedRoleId(roleId);
    if (!isSiteRestrictedRole(roleId)) {
      form.setFieldsValue({ siteId: null });
    }
    // 新規作成時のみデフォルトセッション期間を設定
    if (editingUser === null) {
      const defaultDuration = getDefaultSessionDurationByRole(roleId);
      form.setFieldsValue({ sessionDurationMinutes: defaultDuration });
    }
  };

  // モーダルOK
  const handleModalOk = (): void => {
    form
      .validateFields()
      .then(async (values) => {
        try {
          setIsSubmitting(true);

          if (editingUser !== null) {
            // 更新（パスワード変更は専用モーダルで行う）
            const updateParams: {
              userId: string;
              userName?: string;
              roleId?: number;
              siteId?: number | null;
              isActive?: boolean;
              sessionDurationMinutes?: number | null;
              email?: string | null;
              twoFactorEnabled?: boolean;
            } = {
              userId: editingUser.id,
              userName: values.userName,
              roleId: values.roleId,
              siteId: isSiteRestrictedRole(values.roleId) ? values.siteId : null,
              isActive: values.isActive,
              sessionDurationMinutes: values.sessionDurationMinutes ?? null,
            };

            // DEV_USERの場合のみ2FA設定を更新
            if (values.roleId === ROLE_ID.DEV_USER) {
              updateParams.email = values.email !== '' ? values.email : null;
              updateParams.twoFactorEnabled = values.twoFactorEnabled ?? false;
            }

            await userApiService.updateUser(updateParams);
            message.success('ユーザーを更新しました');
          } else {
            // 新規作成
            if (values.password === undefined || values.password === '') {
              message.error('パスワードは必須です');
              return;
            }

            await userApiService.createUser({
              userName: values.userName,
              password: values.password,
              roleId: values.roleId,
              siteId: isSiteRestrictedRole(values.roleId) ? values.siteId : null,
              isActive: values.isActive,
              sessionDurationMinutes: values.sessionDurationMinutes ?? null,
            });
            message.success('ユーザーを作成しました');
          }

          setIsModalVisible(false);
          form.resetFields();
          setEditingUser(null);
          setSelectedRoleId(null);
          await loadData();
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '不明なエラー';
          message.error(`保存に失敗しました: ${errorMessage}`);
        } finally {
          setIsSubmitting(false);
        }
      })
      .catch(() => {
        // バリデーションエラーはフォームに表示
      });
  };

  // モーダルキャンセル
  const handleModalCancel = (): void => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingUser(null);
    setSelectedRoleId(null);
  };

  // パスワード再設定モーダルを開く
  const handlePasswordReset = (user: User): void => {
    setPasswordResetTargetUser(user);
    passwordResetForm.resetFields();
    setIsPasswordResetModalVisible(true);
  };

  // パスワード再設定実行
  const handlePasswordResetOk = (): void => {
    passwordResetForm
      .validateFields()
      .then(async (values) => {
        if (passwordResetTargetUser === null) {
          return;
        }

        try {
          setIsPasswordResetSubmitting(true);
          await userApiService.updateUser({
            userId: passwordResetTargetUser.id,
            password: values.newPassword,
          });
          message.success(`${passwordResetTargetUser.userName} のパスワードを再設定しました`);
          setIsPasswordResetModalVisible(false);
          passwordResetForm.resetFields();
          setPasswordResetTargetUser(null);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '不明なエラー';
          message.error(`パスワード再設定に失敗しました: ${errorMessage}`);
        } finally {
          setIsPasswordResetSubmitting(false);
        }
      })
      .catch(() => {
        // バリデーションエラーはフォームに表示
      });
  };

  // パスワード再設定モーダルキャンセル
  const handlePasswordResetCancel = (): void => {
    setIsPasswordResetModalVisible(false);
    passwordResetForm.resetFields();
    setPasswordResetTargetUser(null);
  };

  // 2FA切り替え時のハンドラー
  const handleTwoFactorToggle = (checked: boolean): void => {
    if (checked) {
      // 有効化時は確認ダイアログを表示
      setIsTwoFactorConfirmVisible(true);
      // Switchを一旦元に戻す（確認後に変更）
      form.setFieldsValue({ twoFactorEnabled: false });
    }
  };

  // 確認ダイアログでOKした場合
  const handleTwoFactorConfirmOk = (): void => {
    form.setFieldsValue({ twoFactorEnabled: true });
    setIsTwoFactorConfirmVisible(false);
  };

  // 確認ダイアログでキャンセルした場合
  const handleTwoFactorConfirmCancel = (): void => {
    setIsTwoFactorConfirmVisible(false);
  };

  // 信頼済みデバイス一覧を取得
  const loadTrustedDevices = async (userId: string): Promise<void> => {
    try {
      setIsTrustedDevicesLoading(true);
      const devices = await userApiService.getTrustedDevices({ userId });
      setTrustedDevices(devices);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(`信頼済みデバイスの取得に失敗しました: ${errorMessage}`);
    } finally {
      setIsTrustedDevicesLoading(false);
    }
  };

  // 信頼済みデバイス一覧モーダルを開く
  const handleShowTrustedDevices = async (user: User): Promise<void> => {
    setTrustedDevicesTargetUser(user);
    setIsTrustedDevicesModalVisible(true);
    await loadTrustedDevices(user.id);
  };

  // 信頼済みデバイスを削除
  const handleDeleteTrustedDevice = async (deviceId: number): Promise<void> => {
    if (trustedDevicesTargetUser === null) {
      return;
    }

    Modal.confirm({
      title: '信頼済みデバイスの削除',
      content: 'このIPアドレスの信頼を解除しますか？次回このIPからログインする際に2FA認証が必要になります。',
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          await userApiService.deleteTrustedDevice({
            userId: trustedDevicesTargetUser.id,
            deviceId,
          });
          message.success('信頼済みデバイスを削除しました');
          await loadTrustedDevices(trustedDevicesTargetUser.id);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : '不明なエラー';
          message.error(`削除に失敗しました: ${errorMessage}`);
        }
      },
    });
  };

  // 信頼済みデバイス一覧のカラム定義
  const trustedDeviceColumns = [
    {
      title: 'IPアドレス',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
    },
    {
      title: '有効期限',
      dataIndex: 'trustedUntil',
      key: 'trustedUntil',
      render: (date: string) => formatDateTime(new Date(date)),
    },
    {
      title: '最終使用',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (date: string) => formatDateTime(new Date(date)),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: TrustedDevice) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteTrustedDevice(record.id)}
        >
          削除
        </Button>
      ),
    },
  ];

  const columns = [
    {
      title: 'ユーザー名',
      dataIndex: 'userName',
      key: 'userName',
      render: (userName: string) => (
        <Space>
          <UserOutlined />
          <span>{userName}</span>
        </Space>
      ),
    },
    {
      title: 'ロール',
      dataIndex: 'roleId',
      key: 'roleId',
      render: (_roleId: number, record: User) => {
        const tagColor = getRoleTagColor(record.roleId);
        return <Tag color={tagColor}>{record.roleName}</Tag>;
      },
    },
    {
      title: '所属サイト',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (siteName: string | null) => siteName ?? '-',
      responsive: isMobile ? undefined : (['md'] as Array<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'>),
    },
    {
      title: 'ステータス',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'アクティブ' : '無効'}
        </Tag>
      ),
    },
    {
      title: 'セッション期間',
      dataIndex: 'sessionDurationMinutes',
      key: 'sessionDurationMinutes',
      render: (minutes: number | null) => {
        if (minutes === null) {
          return <Tag color="purple">無制限</Tag>;
        }
        if (minutes >= 60) {
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          return remainingMinutes > 0 ? `${hours}時間${remainingMinutes}分` : `${hours}時間`;
        }
        return `${minutes}分`;
      },
      responsive: isMobile ? undefined : (['lg'] as Array<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'>),
    },
    {
      title: '2FA',
      dataIndex: 'twoFactorEnabled',
      key: 'twoFactorEnabled',
      render: (enabled: boolean, record: User) => {
        // DEV_USER以外は表示しない
        if (record.roleId !== ROLE_ID.DEV_USER) {
          return '-';
        }
        return (
          <Tag color={enabled ? 'success' : 'default'}>
            {enabled ? '有効' : '無効'}
          </Tag>
        );
      },
      responsive: isMobile ? undefined : (['md'] as Array<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'>),
    },
    {
      title: '操作',
      key: 'action',
      render: (_text: unknown, record: User) => (
        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size={isMobile ? 'small' : 'middle'}
          >
            編集
          </Button>
          <Button
            type="link"
            icon={<KeyOutlined />}
            onClick={() => handlePasswordReset(record)}
            size={isMobile ? 'small' : 'middle'}
          >
            PW再設定
          </Button>
          {record.roleId === ROLE_ID.DEV_USER && record.twoFactorEnabled && (
            <Button
              type="link"
              icon={<SafetyOutlined />}
              onClick={() => handleShowTrustedDevices(record)}
              size={isMobile ? 'small' : 'middle'}
            >
              信頼済みIP
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const needsSiteSelection = selectedRoleId !== null && isSiteRestrictedRole(selectedRoleId);

  return (
    <div>
      <div className={isMobile ? 'mb-4' : 'flex justify-between items-center mb-6'}>
        <Title level={2} style={isMobile ? { marginBottom: '12px' } : undefined}>
          ユーザー管理
        </Title>
        <Space
          direction={isMobile ? 'vertical' : 'horizontal'}
          style={isMobile ? { width: '100%' } : undefined}
        >
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            loading={isLoading}
            block={isMobile}
          >
            更新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} block={isMobile}>
            ユーザー追加
          </Button>
        </Space>
      </div>

      <Card title="ユーザー一覧" className="mb-6">
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <Space direction="vertical" size="small">
            <Text strong>ロールについて</Text>
            <Text type="secondary">
              <Tag color={getRoleTagColor(ROLE_ID.DEV_USER)}>開発ユーザー</Tag>
              システム全体の管理権限
            </Text>
            <Text type="secondary">
              <Tag color={getRoleTagColor(ROLE_ID.MCM_USER)}>MCMユーザー</Tag>
              全サイトの閲覧・編集権限
            </Text>
            <Text type="secondary">
              <Tag color={getRoleTagColor(ROLE_ID.SITE_USER)}>現場ユーザー</Tag>
              割り当てサイトの閲覧・編集権限
            </Text>
            <Text type="secondary">
              <Tag color={getRoleTagColor(ROLE_ID.SITE_VIEWER)}>現場閲覧者</Tag>
              割り当てサイトの閲覧のみ
            </Text>
          </Space>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <Spin size="large" tip="読み込み中..." />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={users.map((user) => ({ ...user, key: user.id }))}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>

      <Modal
        title={editingUser !== null ? 'ユーザー編集' : 'ユーザー追加'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingUser !== null ? '更新' : '追加'}
        cancelText="キャンセル"
        confirmLoading={isSubmitting}
        okButtonProps={{ disabled: !isUserFormSubmittable }}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="userName"
            label="ユーザー名"
            rules={[
              { required: true, message: 'ユーザー名を入力してください' },
              { min: 3, message: 'ユーザー名は3文字以上で入力してください' },
              { max: 100, message: 'ユーザー名は100文字以下で入力してください' },
            ]}
          >
            <Input placeholder="ユーザー名" prefix={<UserOutlined />} />
          </Form.Item>

          {/* 新規作成時のみパスワードフィールドを表示（編集時はPW再設定モーダルを使用） */}
          {editingUser === null && (
            <Form.Item
              name="password"
              label="パスワード"
              rules={[
                { required: true, message: 'パスワードを入力してください' },
                { min: 8, message: 'パスワードは8文字以上で入力してください' },
              ]}
            >
              <Input.Password placeholder="パスワード" />
            </Form.Item>
          )}

          <Form.Item
            name="roleId"
            label="ロール"
            rules={[{ required: true, message: 'ロールを選択してください' }]}
          >
            <Select placeholder="ロールを選択" onChange={handleRoleChange}>
              {roles.map((role) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.roleName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {needsSiteSelection && (
            <Form.Item
              name="siteId"
              label="所属サイト"
              rules={[{ required: true, message: '所属サイトを選択してください' }]}
            >
              <Select placeholder="サイトを選択">
                {sites.map((site) => (
                  <Select.Option key={site.id} value={site.id}>
                    {site.siteName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="isActive" label="ステータス" valuePropName="checked">
            <Switch checkedChildren="アクティブ" unCheckedChildren="無効" />
          </Form.Item>

          <Form.Item
            name="sessionDurationMinutes"
            label="セッション期間（分）"
            tooltip="空欄の場合は無制限になります"
            rules={[
              {
                type: 'integer',
                min: SESSION_DURATION_MIN_MINUTES,
                max: SESSION_DURATION_MAX_MINUTES,
                message: `セッション期間は${SESSION_DURATION_MIN_MINUTES}以上${SESSION_DURATION_MAX_MINUTES}以下の整数（分）で入力してください`,
              },
            ]}
          >
            <InputNumber
              placeholder="空欄で無制限"
              style={{ width: '100%' }}
              addonAfter="分"
            />
          </Form.Item>

          {/* DEV_USERの場合のみ2FA設定を表示 */}
          {selectedRoleId === ROLE_ID.DEV_USER && (
            <>
              <Form.Item
                name="email"
                label="メールアドレス（2FA用）"
                tooltip="2要素認証の認証コード送信先"
                rules={[
                  {
                    type: 'email',
                    message: '有効なメールアドレスを入力してください',
                  },
                  {
                    max: 254,
                    message: 'メールアドレスは254文字以下で入力してください',
                  },
                ]}
              >
                <Input
                  placeholder="example@domain.com"
                  prefix={<MailOutlined />}
                />
              </Form.Item>

              <Form.Item
                name="twoFactorEnabled"
                label="2要素認証"
                valuePropName="checked"
                tooltip="メールアドレス設定後に有効化できます"
              >
                <Switch
                  checkedChildren="有効"
                  unCheckedChildren="無効"
                  disabled={!emailValue}
                  onChange={handleTwoFactorToggle}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* パスワード再設定モーダル */}
      <Modal
        title={`パスワード再設定: ${passwordResetTargetUser?.userName ?? ''}`}
        open={isPasswordResetModalVisible}
        onOk={handlePasswordResetOk}
        onCancel={handlePasswordResetCancel}
        okText="再設定"
        cancelText="キャンセル"
        confirmLoading={isPasswordResetSubmitting}
        width={400}
      >
        <Form form={passwordResetForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="新しいパスワード"
            rules={[
              { required: true, message: '新しいパスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上で入力してください' },
            ]}
          >
            <Input.Password placeholder="新しいパスワード" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="パスワード確認"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'パスワード確認を入力してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="パスワード確認" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 2FA有効化確認ダイアログ */}
      <Modal
        title="2要素認証を有効化"
        open={isTwoFactorConfirmVisible}
        onOk={handleTwoFactorConfirmOk}
        onCancel={handleTwoFactorConfirmCancel}
        okText="有効化する"
        cancelText="キャンセル"
      >
        <Space direction="vertical">
          <Text>
            2要素認証を有効化すると、次回ログイン時から
            メールで送信される認証コードの入力が必要になります。
          </Text>
          <Text type="warning">
            <WarningOutlined /> メールアドレスが正しく設定されていることを
            確認してください。
          </Text>
        </Space>
      </Modal>

      {/* 信頼済みデバイス一覧モーダル */}
      <Modal
        title={`信頼済みデバイス: ${trustedDevicesTargetUser?.userName ?? ''}`}
        open={isTrustedDevicesModalVisible}
        onCancel={() => setIsTrustedDevicesModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsTrustedDevicesModalVisible(false)}>
            閉じる
          </Button>,
        ]}
        width={700}
      >
        {isTrustedDevicesLoading ? (
          <div className="text-center py-4">
            <Spin tip="読み込み中..." />
          </div>
        ) : trustedDevices.length === 0 ? (
          <Text type="secondary">信頼済みデバイスはありません</Text>
        ) : (
          <Table
            columns={trustedDeviceColumns}
            dataSource={trustedDevices.map((device) => ({ ...device, key: device.id }))}
            pagination={false}
            size="small"
          />
        )}
      </Modal>
    </div>
  );
};
